import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Input,
  Button,
  List,
  Spin,
  Card,
  Switch,
  Flex,
  Checkbox,
  Slider,
} from "antd"; // Importing Ant Design components
import {
  useUpdateChecklistMutation,
  useFetchChecklistQuery,
} from "../opencheck/checklists.service"; // Adjust import to your API
import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./style.css";
import TextEditor from "../../components/TextEditor";

const WeeklycheckUpdate = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const navigate = useNavigate();

  const { id } = useParams();

  const [newChecklist, setNewChecklist] = useState("");
  const [isInput, setIsInput] = useState(false);
  const [record, setRecord] = useState("");
  const [isSection, setIsSection] = useState(false);
  const [isTempSlider, setIsTempSlider] = useState(false);
  const [tempSlider, setTempSlider] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [checkContent, setCheckContent] = useState();
  const {
    data: checklist = {},
    isFetching,
    isSuccess,
  } = useFetchChecklistQuery(id, { refetchOnMountOrArgChange: true }); // Adjust based on your data fetching logic
  const [updateChecklist, { isLoading: isEditLoading }] =
    useUpdateChecklistMutation();

  useEffect(() => {
    if (isSuccess) {
      setNewChecklist(checklist[`title_${language}`] || checklist[`title_en`]);
      setIsInput(checklist.isInput);
      setIsCompleted(checklist.status === "completed" ? true : false);
      setIsTempSlider(checklist.isTempSlider);
      setTempSlider(checklist.tempSlider);
      if (checklist.isDateRecord) setRecord("recordTD");
      if (checklist.isTemperature) setRecord("recordTemp");
      setCheckContent(
        checklist[`content_${language}`] || checklist[`content_en`]
      );
      setIsSection(checklist.isSection);
    }
  }, [checklist]);

  const handleUpdate = async () => {
    // You can implement update logic here
    try {
      await updateChecklist({
        id,
        data: {
          isDateRecord: record === "recordTD" && true,
          isTemperature: record === "recordTemp" && true,
          isInput,
          isSection,
          isTempSlider,
          tempSlider,
          title: newChecklist,
          status: isCompleted ? "completed" : "pending",
          content:
            isInput && checkContent && checkContent !== "" && checkContent,
        },
      }); // Implement actual update logic
    } catch (err) {
      console.log("Failed to update checklist");
    }
  };

  return (
    <Spin spinning={isFetching}>
      <Card
        title={t("OPENCHECK EDIT")}
        style={{ fontSize: "20px" }}
        bordered={false}
        className="opencheck-create-page"
        extra={
          <Flex justify="center">
            <Button type="default" onClick={() => navigate("/weeklychecks")}>
              {t("BACK")}
            </Button>
          </Flex>
        }
      >
        <div className="opencheck-create-page-container">
          <TextEditor
            newChecklist={newChecklist}
            setNewChecklist={setNewChecklist}
          />
          <div className="opencheck-create-page-container-switch">
            <h4>{t("ADD TEXTBOX")}</h4>
            <Switch
              value={isInput}
              onChange={(e) => {
                if (e) setIsSection(false);
                setIsInput(e);
              }}
            />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{t("RECORD TEMPERATURES")}</h4>
            <Switch
              value={record === "recordTemp" ? true : false}
              onChange={(e) => {
                if (e) setIsSection(false);
                setRecord(e ? "recordTemp" : "");
              }}
            />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{t("RECORD TIME & DATE")}</h4>
            <Switch
              value={record === "recordTD" ? true : false}
              onChange={(e) => {
                if (e) setIsSection(false);
                setRecord(e ? "recordTD" : "");
              }}
            />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{t("ADD TEMPERATURE SLIDER")}</h4>
            <Switch
              value={isTempSlider}
              onChange={(e) => {
                if (e) setIsSection(false);
                setIsTempSlider(e);
              }}
            />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{t("SECTION")}</h4>
            <Switch
              value={isSection}
              onChange={(e) => {
                if (e) {
                  setIsInput(false);
                  setRecord("");
                  setIsTempSlider(false);
                  setTempSlider(0);
                }
                setIsSection(e);
              }}
            />
          </div>
          <div className="opencheck-create-page-container-button">
            <Button
              color="green"
              variant="solid"
              icon={<EditOutlined />}
              size="small"
              onClick={handleUpdate}
              loading={isEditLoading}
            >
              {t("Update")}
            </Button>
          </div>
        </div>

        <List
          className="opencheck-create-page-insight"
          itemLayout="horizontal"
          dataSource={[
            {
              isSection: isSection,
              title: isSection ? "" : newChecklist,
              description: isSection ? (
                <div
                  className="compliance-checklist-content"
                  dangerouslySetInnerHTML={{
                    __html: newChecklist,
                  }}
                ></div>
              ) : (
                <div className="opening-check-add-task">
                  {isInput && (
                    <div style={{ padding: "5px 15px" }}>
                      <Input
                        style={{ background: "transparent" }}
                        value={checkContent}
                        onChange={(e) => setCheckContent(e.target.value)}
                      />
                    </div>
                  )}
                  {isTempSlider && (
                    <>
                      <div
                        style={{
                          fontSize: "1.5rem",
                          textAlign: "center",
                          marginTop: 10,
                          fontWeight: "bold",
                        }}
                      >
                        {tempSlider.toFixed(1)}
                        °C
                      </div>
                      <Slider
                        min={-30}
                        max={120}
                        step={0.1}
                        onChange={(temp) => setTempSlider(temp)}
                        defaultValue={tempSlider}
                      />
                    </>
                  )}
                  {record === "recordTD" && (
                    <div>*RECORD TIME AND DATE AUTOMATICALLY*</div>
                  )}
                  {record === "recordTemp" && (
                    <div>*RECORD FRIDGE TEMPERATURES AUTOMATICALLY*</div>
                  )}
                </div>
              ),
            },
          ]}
          renderItem={(checklist, index) => (
            <List.Item
              className={
                checklist.title === ""
                  ? "background-section"
                  : "background-task"
              }
              actions={[
                <Checkbox
                  checked={isCompleted}
                  onChange={(e) => setIsCompleted(e.target.checked)}
                />,
              ]}
            >
              <List.Item.Meta
                title={
                  checklist.title !== "" && (
                    <div
                      className="compliance-checklist-content"
                      dangerouslySetInnerHTML={{ __html: checklist.title }}
                    ></div>
                  )
                }
                description={checklist.description}
              />
            </List.Item>
          )}
        />
      </Card>
    </Spin>
  );
};

export default WeeklycheckUpdate;
